import { useState, useEffect } from "react";
import { Lightbulb, ChevronDown, ChevronUp, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const COACH_COLLAPSED_KEY = "scribpost-coach-collapsed";

interface PostCoachProps {
  tip: string | null;
  isLoading: boolean;
  className?: string;
  onDismiss?: () => void;
}

export function PostCoach({ tip, isLoading, className = "", onDismiss }: PostCoachProps) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COACH_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COACH_COLLAPSED_KEY, String(next));
    } catch {
    }
  };

  useEffect(() => {
    if (tip) {
      setCollapsed(false);
      try {
        localStorage.setItem(COACH_COLLAPSED_KEY, "false");
      } catch {
      }
    }
  }, [tip]);

  if (!isLoading && !tip) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.25 }}
      className={`rounded-2xl border border-amber-500/20 bg-amber-500/5 overflow-hidden ${className}`}
      data-testid="post-coach-card"
    >
      <div className="flex items-center gap-2.5 px-4 py-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Lightbulb
            className={`w-4 h-4 shrink-0 ${isLoading ? "text-amber-400/50 animate-pulse" : "text-amber-400"}`}
          />
          <span className="text-xs font-semibold text-amber-300 tracking-wide uppercase">
            AI Coach
          </span>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {tip && (
            <button
              type="button"
              onClick={toggleCollapsed}
              className="p-1 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
              aria-label={collapsed ? "Expand coaching tip" : "Collapse coaching tip"}
              data-testid="coach-toggle-btn"
            >
              {collapsed ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronUp className="w-3.5 h-3.5" />
              )}
            </button>
          )}
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="p-1 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Dismiss coaching tip"
              data-testid="coach-dismiss-btn"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            key="coach-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              {isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce [animation-delay:0ms]" />
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce [animation-delay:150ms]" />
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce [animation-delay:300ms]" />
                  <span className="ml-1 text-xs text-muted-foreground/70">Analyzing your post…</span>
                </div>
              ) : (
                <p
                  className="text-sm text-foreground/90 leading-relaxed"
                  data-testid="coach-tip-text"
                >
                  {tip}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
